import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import AdminLayout from '../../components/admin/AdminLayout';
import { AGE_GROUPS, TIERS, POSITIONS } from '../../utils/helpers';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';

// Note: We still need direct Firestore access for querying coaches from the users collection

function ManageTeams() {
  const { clubId, club } = useClub();
  
  // Use tenant-scoped data
  const { data: teams, loading, setData: setTeams } = useTenantCollection('teams');
  const { add, update, remove } = useTenantCrud('teams');
  
  const [coaches, setCoaches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  
  const emptyTeam = {
    name: '',
    ageGroup: '',
    tier: '',
    coach: '',
    assistant: '',
    training: '',
    players: []
  };
  
  const [formData, setFormData] = useState(emptyTeam);
  const [newPlayer, setNewPlayer] = useState({ name: '', position: '', number: '' });

  // Fetch coaches (need to filter by clubId as well)
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!clubId) return;
      try {
        const coachQuery = query(
          collection(db, 'users'), 
          where('role', '==', 'coach'),
          where('clubId', '==', clubId)
        );
        const coachSnapshot = await getDocs(coachQuery);
        const coachList = coachSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCoaches(coachList);
      } catch (error) {
        console.error('Error fetching coaches:', error);
      }
    };
    fetchCoaches();
  }, [clubId]);

  const openAddModal = () => {
    setEditingTeam(null);
    setFormData(emptyTeam);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name || '',
      ageGroup: team.ageGroup || '',
      tier: team.tier || '',
      coach: team.coach || '',
      assistant: team.assistant || '',
      training: team.training || '',
      players: team.players || []
    });
    setError('');
    setShowModal(true);
  };

  const openRosterModal = (team) => {
    setSelectedTeam(team);
    setNewPlayer({ name: '', position: '', number: '' });
    setShowRosterModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const teamData = {
        ...formData
      };

      if (editingTeam) {
        // Update existing team (tenant-scoped)
        const updated = await update(editingTeam.id, teamData);
        setTeams(teams.map(t => 
          t.id === editingTeam.id ? updated : t
        ));
      } else {
        // Add new team (tenant-scoped)
        teamData.players = [];
        const newTeam = await add(teamData);
        setTeams([...teams, newTeam]);
      }

      setShowModal(false);
      setFormData(emptyTeam);
    } catch (error) {
      console.error('Error saving team:', error);
      setError('Failed to save team. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPlayer = async () => {
    if (!newPlayer.name || !newPlayer.position || !newPlayer.number) {
      setError('Please fill in all player fields');
      return;
    }

    const updatedPlayers = [...(selectedTeam.players || []), {
      id: Date.now(),
      ...newPlayer,
      number: parseInt(newPlayer.number)
    }];

    try {
      // Update team with new player (tenant-scoped)
      await update(selectedTeam.id, { players: updatedPlayers });

      setTeams(teams.map(t => 
        t.id === selectedTeam.id ? { ...t, players: updatedPlayers } : t
      ));
      setSelectedTeam({ ...selectedTeam, players: updatedPlayers });
      setNewPlayer({ name: '', position: '', number: '' });
      setError('');
    } catch (error) {
      console.error('Error adding player:', error);
      setError('Failed to add player');
    }
  };

  const handleRemovePlayer = async (playerId) => {
    const updatedPlayers = selectedTeam.players.filter(p => p.id !== playerId);

    try {
      // Update team with removed player (tenant-scoped)
      await update(selectedTeam.id, { players: updatedPlayers });

      setTeams(teams.map(t => 
        t.id === selectedTeam.id ? { ...t, players: updatedPlayers } : t
      ));
      setSelectedTeam({ ...selectedTeam, players: updatedPlayers });
    } catch (error) {
      console.error('Error removing player:', error);
    }
  };

  const handleDelete = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete ${teamName}?`)) return;
    
    try {
      // Delete team (tenant-scoped)
      await remove(teamId);
      setTeams(teams.filter(t => t.id !== teamId));
    } catch (error) {
      console.error('Error deleting team:', error);
    }
  };

  return (
    <AdminLayout title="Manage Teams" subtitle={`Create and manage team rosters for ${club?.name || 'your club'}`}>
      <div className="admin-page-header">
        <div></div>
        <button className="btn-add" onClick={openAddModal}>
          + Add Team
        </button>
      </div>

      {/* Teams Grid */}
      <div className="teams-grid">
        {loading ? (
          <p className="loading-text">Loading teams...</p>
        ) : teams.length === 0 ? (
          <div className="admin-card">
            <div className="empty-state">
              <span className="empty-icon">👥</span>
              <h3>No Teams Yet</h3>
              <p>Create your first team to get started.</p>
              <button className="btn-add" onClick={openAddModal}>
                + Add Team
              </button>
            </div>
          </div>
        ) : (
          teams.map(team => (
            <div className="team-card" key={team.id}>
              <div className="team-card-header">
                <h3>{team.name}</h3>
                <span className="team-badge">{team.ageGroup} {team.tier}</span>
              </div>
              <div className="team-card-body">
                <div className="team-info">
                  <p><strong>Coach:</strong> {team.coach || 'Not assigned'}</p>
                  <p><strong>Assistant:</strong> {team.assistant || 'None'}</p>
                  <p><strong>Training:</strong> {team.training || 'TBD'}</p>
                </div>
                <div className="team-roster-preview">
                  <span className="roster-count">{team.players?.length || 0} players</span>
                </div>
              </div>
              <div className="team-card-actions">
                <button className="btn-roster" onClick={() => openRosterModal(team)}>
                  Manage Roster
                </button>
                <button className="btn-edit" onClick={() => openEditModal(team)}>
                  Edit
                </button>
                <button className="btn-delete" onClick={() => handleDelete(team.id, team.name)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Team Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeam ? 'Edit Team' : 'Add New Team'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                
                <div className="form-group">
                  <label>Team Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., U16 Tier 1"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Age Group *</label>
                    <select
                      value={formData.ageGroup}
                      onChange={e => setFormData({...formData, ageGroup: e.target.value})}
                      required
                    >
                      <option value="">Select...</option>
                      {AGE_GROUPS.map(ag => (
                        <option key={ag} value={ag}>{ag}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tier *</label>
                    <select
                      value={formData.tier}
                      onChange={e => setFormData({...formData, tier: e.target.value})}
                      required
                    >
                      <option value="">Select...</option>
                      {TIERS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Head Coach</label>
                  <select
                    value={formData.coach}
                    onChange={e => setFormData({...formData, coach: e.target.value})}
                  >
                    <option value="">Select coach...</option>
                    {coaches.map(c => (
                      <option key={c.id} value={`${c.firstName} ${c.lastName}`}>
                        {c.firstName} {c.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Assistant Coach</label>
                  <select
                    value={formData.assistant}
                    onChange={e => setFormData({...formData, assistant: e.target.value})}
                  >
                    <option value="">Select assistant...</option>
                    {coaches.map(c => (
                      <option key={c.id} value={`${c.firstName} ${c.lastName}`}>
                        {c.firstName} {c.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Training Schedule</label>
                  <input
                    type="text"
                    value={formData.training}
                    onChange={e => setFormData({...formData, training: e.target.value})}
                    placeholder="e.g., Tue & Thu 5:00 PM"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : (editingTeam ? 'Update Team' : 'Add Team')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Roster Modal */}
      {showRosterModal && selectedTeam && (
        <div className="modal-overlay" onClick={() => setShowRosterModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedTeam.name} - Roster</h2>
              <button className="modal-close" onClick={() => setShowRosterModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error">{error}</div>}
              
              {/* Add Player Form */}
              <div className="add-player-form">
                <h4>Add Player to Roster</h4>
                <div className="form-row form-row-3">
                  <input
                    type="text"
                    value={newPlayer.name}
                    onChange={e => setNewPlayer({...newPlayer, name: e.target.value})}
                    placeholder="Player name"
                  />
                  <select
                    value={newPlayer.position}
                    onChange={e => setNewPlayer({...newPlayer, position: e.target.value})}
                  >
                    <option value="">Position...</option>
                    {POSITIONS.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={newPlayer.number}
                    onChange={e => setNewPlayer({...newPlayer, number: e.target.value})}
                    placeholder="#"
                    min="1"
                    max="99"
                  />
                  <button type="button" className="btn-add-small" onClick={handleAddPlayer}>
                    Add
                  </button>
                </div>
              </div>

              {/* Current Roster */}
              <div className="roster-list">
                <h4>Current Roster ({selectedTeam.players?.length || 0} players)</h4>
                {(!selectedTeam.players || selectedTeam.players.length === 0) ? (
                  <p className="no-players">No players on this roster yet.</p>
                ) : (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Position</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTeam.players.map(player => (
                        <tr key={player.id}>
                          <td>{player.number}</td>
                          <td>{player.name}</td>
                          <td><span className={`position-badge ${player.position.toLowerCase()}`}>{player.position}</span></td>
                          <td>
                            <button 
                              className="btn-delete-small"
                              onClick={() => handleRemovePlayer(player.id)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-save" onClick={() => setShowRosterModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManageTeams;
